import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Select,
  Spin,
  Table,
  Tag,
  DatePicker
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import transactions from '@/core/transactions/transactions';
import NumberRound from "number-zero-round";
import ShowDocument from "@/components/invoices/ShowDocument";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;
const { Option } = Select;

const TransactionsTable = ({ paid, unpaid }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [documentNumber, setDocumentNumber] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  const showDocumentHandler = (e) => {
    message.info(t("loading-document"));
    setSelectedNumber(e);
    setShowDocumentModal(true);
  };

  const columns = [
    {
      title: t("invoice-number"),
      dataIndex: "DocumentNumber",
      key: "DocumentNumber",
      sorter: (a, b) => a.DocumentNumber - b.DocumentNumber
    },
    {
      title: t("invoice-date"),
      dataIndex: "DocumentDate",
      key: "DocumentDate",
      sorter: (a, b) => moment(a.DocumentDate).unix() - moment(b.DocumentDate).unix(),
      render: (a) => moment(a).format('DD/MM/YYYY')
    },
    {
      title: t("total-net-price"),
      dataIndex: "TotalNetPrice",
      key: "TotalNetPrice",
      render: (a) => NumberRound(a, 2),
      sorter: (a, b) => a.TotalNetPrice - b.TotalNetPrice
    },
    {
      title: t("total-amount"),
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      sorter: (a, b) => a.TotalAmount - b.TotalAmount,
      render: (a) => '€' + NumberRound(a)
    },
    {
      title: t("paid-amount"),
      dataIndex: "PaidAmount",
      key: "PaidAmount",
      sorter: (a, b) => a.PaidAmount - b.PaidAmount,
      render: (a) => '€' + NumberRound(a)
    },
    {
      title: t("unpaid-amount"),
      key: "UnpaidAmount",
      sorter: (a, b) => a.UnpaidAmount - b.UnpaidAmount,
      render: (_, b) => '€' + NumberRound(b.UnpaidAmount)
    },
    {
      title: t("status"),
      key: "Status",
      sorter: (a, b) => a.UnpaidAmount - b.UnpaidAmount,
      render: (_, b) => (
        <Tag color={Number(b.UnpaidAmount) === 0 ? "green" : "red"}>
          {Number(b.UnpaidAmount) === 0 ? t("paid") : t("unpaid")}
        </Tag>
      )
    },
    {
      title: "",
      key: "download",
      render: (_, b) => (
        <div onClick={() => showDocumentHandler(b.DocumentNumber)}>
          <DownloadOutlined style={{ fontSize: 19, color: "var(--primaryColor)", cursor: "pointer" }} />
        </div>
      )
    }
  ];

  const fetch = (params) => {
    setLoading(true);
    transactions.fetchInvoices(params)
      .then((res) => {
        setData(res.data.data);
        setTotalPage(res.data.totalCount);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || t("error-fetch-failed"));
      })
      .finally(() => setLoading(false));
  };

  const onFinish = () => {
    const params = {
      currPage: 1,
      limit: pageSize,
      ...(startDate && { startDate: startDate.format("YYYY-MM-DD HH:mm:ss") }),
      ...(endDate && { endDate: endDate.format("YYYY-MM-DD HH:mm:ss") }),
      ...(documentNumber && { documentNumber }),
      paid,
      unpaid
    };
    setPage(1);
    fetch(params);
  };

  useEffect(() => {
    fetch({ currPage: 1, paid, unpaid, limit: pageSize });
  }, []);

  return (
    <Spin spinning={loading}>
      <Modal
        bodyStyle={{ height: '1000px', padding: 0 }}
        open={showDocumentModal}
        footer={null}
        width="100%"
        onCancel={() => setShowDocumentModal(false)}
      >
        <ShowDocument documentNumber={selectedNumber} type={1} />
      </Modal>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="DocumentNumber" label={t("document-number")}>
          <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} />
        </Form.Item>

        <Form.Item label={t("date")}>
          <RangePicker
            onChange={(e) => {
              if (e) {
                setStartDate(e[0]);
                setEndDate(e[1]);
              } else {
                setStartDate(null);
                setEndDate(null);
              }
            }}
            showTime
          />
        </Form.Item>

        <Form.Item>
          <button className="mega-button" onClick={onFinish}>
            {t("search")}
          </button>
        </Form.Item>
      </Form>

      <Table
        uid="DocumentNumber"
        size="small"
        className="table-2"
        columns={columns}
        dataSource={data}
        pagination={false}
        summary={() => {
          const net = data.reduce((sum, item) => sum + parseFloat(item.TotalNetPrice || 0), 0);
          const paidSum = data.reduce((sum, item) => sum + parseFloat(item.PaidAmount || 0), 0);
          const unpaidSum = data.reduce((sum, item) => sum + parseFloat(item.UnpaidAmount || 0), 0);
          return (
            <Table.Summary.Row style={{ backgroundColor: "#F2F5F6" }}>
              <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
              <Table.Summary.Cell />
              <Table.Summary.Cell />
              <Table.Summary.Cell><div style={{ color: "#088F8F" }}>{'€' + NumberRound(net)}</div></Table.Summary.Cell>
              <Table.Summary.Cell><div style={{ color: "green" }}>{'€' + NumberRound(paidSum)}</div></Table.Summary.Cell>
              <Table.Summary.Cell><div style={{ color: "red" }}>{'€' + NumberRound(unpaidSum)}</div></Table.Summary.Cell>
              <Table.Summary.Cell colSpan={2} />
            </Table.Summary.Row>
          );
        }}
        title={() => (
          <div>
            <span>{t("show-per-page")}:</span>
            <Select style={{ width: 200, marginLeft: 10 }} value={pageSize} onChange={(e) => {
              setPageSize(e);
              fetch({ currPage: 1, paid, unpaid, limit: e });
            }}>
              <Option value={10}>10</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
              <Option value={500}>500</Option>
            </Select>
          </div>
        )}
      />

      {!loading && (
        <Pagination
          className="mt-3"
          current={page}
          total={totalPage}
          pageSize={pageSize}
          onChange={(e) => {
            setPage(e);
            fetch({ currPage: e, paid, unpaid });
          }}
        />
      )}
    </Spin>
  );
};

export default TransactionsTable;
